import React from 'react'
import PropTypes from 'prop-types'
import EventBlocker from 'components/common/EventBlocker'
import { Chip, IconButton, LinearProgress, ListItem, ListItemIcon, ListItemText, Skeleton, Stack, Typography } from '@mui/material'
import LateFeesPopover from 'components/pricing/LateFees/LateFeesListing/LateFeesPopover'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ChevronRight from '@mui/icons-material/ChevronRight'
import InvoiceStatusChip from 'components/common/chip/InvoiceStatusChip'
import { AGING_BUCKET_COLORS } from 'components/common/aging-bucket/settings'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import T from 'T'
import { orange } from '@mui/material/colors'
import { formatDateToFEFormat } from 'utils/date'
import { getInvoiceIsPartialStatusAndBucket } from 'utils/agingBucket'
import { INVOICE_TYPE } from 'settings/constants/billing'
import { deriveInvoicePeriodLabel } from 'data/billing-period/invoicePeriodLabel'
import { noop } from 'lodash'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { getIsCreditMemo, getInvoiceText, getAmount } from 'utils/creditMemo'
import { isAllowNegativeInvoiceFeatureAvailable } from 'data/launch-darkly/featureSelectors'

const { MANUAL } = INVOICE_TYPE

const InvoiceAccordionSummary = ({
  invoice,
  isViewOnly = false,
  onActionClick = noop,
  onExpandClick = noop,
  isExpanded = false,
  allPayments = [],
  emailEvents = [],
  expandable = true,
  isGenerating = false,
}) => {
  const {
    id,
    invoiceNumber,
    invoiceDate,
    dueDate,
    amountPaidCents,
    totalCents,
    startDate,
    endDate,
    type,
    status,
    lateFeeTargets = 0,
    latestBatchStatus,
    latestBatchFailure,
  } = invoice

  const transformDate = date => (date ? formatDateToFEFormat(date) : '-')

  const { label: title } = deriveInvoicePeriodLabel({ startDate, endDate, invoiceDate, type })
  const pendingAmount = totalCents - amountPaidCents
  const { isPartial, agingBucketType } = getInvoiceIsPartialStatusAndBucket(status, dueDate, amountPaidCents, totalCents)

  const flags = useFlags()
  const isAllowNegativeInvoiceEnabled = isAllowNegativeInvoiceFeatureAvailable({ flags })
  const isCreditMemo = isAllowNegativeInvoiceEnabled ? getIsCreditMemo(totalCents) : false
  const invoiceText = getInvoiceText(title, isCreditMemo)
  const totalAmount = getAmount(totalCents, isCreditMemo)

  return (
    <ListItem
      sx={{
        position: 'relative',
        opacity: isGenerating ? 0.6 : 1,
        transition: 'opacity 0.3s',
        pointerEvents: isGenerating ? 'none' : undefined,
      }}
      secondaryAction={
        !isGenerating && (
          <EventBlocker>
            <Stack direction="row" alignItems="center" gap={1}>
              <LateFeesPopover invoiceId={id} lateFeeTargets={lateFeeTargets} />
              {!isViewOnly && (
                <IconButton size="small" onClick={onActionClick}>
                  <MoreVertIcon />
                </IconButton>
              )}
            </Stack>
          </EventBlocker>
        )
      }
    >
      {isGenerating && <LinearProgress aria-label="Invoice generating" sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />}
      <ListItemIcon sx={{ mr: 7 }}>
        <Stack direction="row" alignItems="center" gap={2}>
          {expandable && !isGenerating && (
            <EventBlocker>
              <IconButton size="small" onClick={onExpandClick}>
                {isExpanded ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
            </EventBlocker>
          )}
          <Stack direction="column" gap={1}>
            <InvoiceStatusChip
              status={status}
              isPartial={isPartial}
              agingBucketType={agingBucketType}
              generationStatus={latestBatchStatus}
              generationFailure={latestBatchFailure}
            />
            {isPartial && agingBucketType && (
              <Typography color={AGING_BUCKET_COLORS[agingBucketType][700]} variant="caption" fontWeight={700}>
                <HHDisplayMoney value={pendingAmount} /> left
              </Typography>
            )}
          </Stack>
        </Stack>
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="h4" fontWeight={600}>
              {`${invoiceText} | `}
            </Typography>
            <Typography variant="h4" fontWeight={600}>
              {isGenerating ? <Skeleton variant="text" width={80} /> : <HHDisplayMoney value={totalAmount} />}
            </Typography>
            {type === MANUAL && <Chip label={T.MANUAL} size="small" sx={{ backgroundColor: orange[100], ml: 1, borderRadius: 1 }} />}
          </Stack>
        }
        secondary={
          <Typography variant="subtitle2">
            {`#${invoiceNumber} // Invoice date: ${transformDate(invoiceDate)} | Due: ${transformDate(dueDate)}`}
          </Typography>
        }
      />
    </ListItem>
  )
}

InvoiceAccordionSummary.propTypes = {
  invoice: PropTypes.object,
  isViewOnly: PropTypes.bool,
  onActionClick: PropTypes.func,
  onExpandClick: PropTypes.func,
  isExpanded: PropTypes.bool,
  allPayments: PropTypes.array,
  emailEvents: PropTypes.array,
  expandable: PropTypes.bool,
  isGenerating: PropTypes.bool,
}

export default InvoiceAccordionSummary
